
import { UserProduct } from '@/types';
import { offlineDb, SyncableUserProduct } from '@/db/offlineDb';
import OfflineLogger from '@/utils/offlineLogger';
import { normalizePhotoUrl } from '@/utils/normalizePhotoUrl';

// Helper functions to convert between UserProduct and SyncableProduct
const normalizeProductName = (name: string): string => name.trim().toLowerCase();

const convertToSyncableProduct = (product: UserProduct): SyncableUserProduct => ({
  id: product.id,
  name: product.name,
  normalized_name: normalizeProductName(product.name),
  user_id: product.userId,
  default_quantity: product.defaultQuantity,
  default_unit: product.defaultUnit,
  photo: normalizePhotoUrl(product.photo),
  category_id: product.categoryId,
  barcode: product.barcode,
  openfoodfacts_id: product.openfoodfacts_id,
  attributes: product.attributes,
  created_at: product.createdAt,
  updated_at: new Date().toISOString(),
  _syncStatus: 'synced' as const,
  _lastModified: Date.now()
});

const convertToUserProduct = (syncableProduct: SyncableUserProduct): UserProduct => ({
  id: syncableProduct.id,
  userId: syncableProduct.user_id || '',
  name: syncableProduct.name,
  defaultQuantity: syncableProduct.default_quantity,
  defaultUnit: syncableProduct.default_unit,
  photo: normalizePhotoUrl(syncableProduct.photo),
  categoryId: syncableProduct.category_id,
  barcode: syncableProduct.barcode,
  openfoodfacts_id: syncableProduct.openfoodfacts_id,
  attributes: syncableProduct.attributes || [],
  createdAt: syncableProduct.created_at || new Date().toISOString()
});

export class ProductRepository {
  protected tableName = 'user_products';
  protected dbTable = offlineDb.products;
  
  async create(product: UserProduct): Promise<UserProduct> {
    try {
      // ✅ MIGLIORATO: Check più intelligente per duplicati basato su contenuto
      const existing = await this.findDuplicateByContent(product);
      if (existing) {
        //OfflineLogger.info('ProductRepository', `Duplicate content found, updating instead: ${product.id}`);
        
        // Se il prodotto esistente è più vecchio, aggiorna con i nuovi dati
        if (existing._lastModified < Date.now() - 1000) { // 1 secondo di tolleranza
          return this.update(existing.id, product) || product;
        } else {
          // Se è recente, restituisci l'esistente
          return convertToUserProduct(existing);
        }
      }

      const syncableProduct = convertToSyncableProduct(product);
      
      // Mark as pending only for offline-created products
      if (this.isOfflineCreatedProduct(product.id)) {
        syncableProduct._syncStatus = 'pending';
      }
      
      await this.dbTable.put(syncableProduct);
      //OfflineLogger.info('ProductRepository', `Created product: ${product.id}`);
      return product;
    } catch (error) {
      OfflineLogger.error('ProductRepository.create', error);
      throw error;
    }
  }

  async getAll(): Promise<UserProduct[]> {
    try {
      const syncableProducts = await this.dbTable
        .where('_syncStatus')
        .notEqual('deleted')
        .toArray();
        
      // ✅ MIGLIORATO: Remove duplicates con algoritmo più intelligente
      const uniqueProducts = this.removeDuplicatesIntelligent(syncableProducts);
      
      return uniqueProducts.map(convertToUserProduct);
    } catch (error) {
      OfflineLogger.error('ProductRepository.getAll', error);
      return [];
    }
  }
  
  async getByBarcode(barcode: string): Promise<UserProduct | null> {
    try {
      const products = await this.dbTable
        .where('barcode')
        .equals(barcode)
        .and(item => item._syncStatus !== 'deleted')
        .toArray();
        
      // ✅ NUOVO: Se ci sono duplicati, prendi il più recente
      if (products.length > 1) {
        products.sort((a, b) => b._lastModified - a._lastModified);
      }
        
      return products.length > 0 ? convertToUserProduct(products[0]) : null;
    } catch (error) {
      OfflineLogger.error('ProductRepository.getByBarcode', error);
      return null;
    }
  }

  async getByCategory(categoryId: string): Promise<UserProduct[]> {
    try {
      const products = await this.dbTable
        .where('category_id')
        .equals(categoryId)
        .and(item => item._syncStatus !== 'deleted')
        .toArray();
      return products.map(convertToUserProduct);
    } catch (error) {
      OfflineLogger.error('ProductRepository.getByCategory', error);
      return [];
    }
  }
  
  async update(id: string, updates: Partial<UserProduct>): Promise<UserProduct | null> {
    try {
      const updateData: Partial<SyncableUserProduct> = {
        name: updates.name,
        default_quantity: updates.defaultQuantity,
        default_unit: updates.defaultUnit,
        photo: normalizePhotoUrl(updates.photo),
        category_id: updates.categoryId,
        barcode: updates.barcode,
        openfoodfacts_id: updates.openfoodfacts_id,
        attributes: updates.attributes,
        updated_at: new Date().toISOString(),
        _syncStatus: 'pending' as const,
        _lastModified: Date.now()
      };

      if (typeof updates.name === 'string') {
        updateData.normalized_name = normalizeProductName(updates.name);
      }
      
      await this.dbTable.update(id, updateData);
      
      const updated = await this.dbTable.get(id);
      return updated ? convertToUserProduct(updated) : null;
    } catch (error) {
      OfflineLogger.error('ProductRepository.update', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      // ✅ NUOVO: Verifica se il prodotto esiste prima di marcarlo come eliminato
      const existing = await this.dbTable.get(id);
      if (!existing) {
        OfflineLogger.info('ProductRepository', `Product not found for deletion: ${id}`);
        return;
      }
      
      await this.dbTable.update(id, {
        _syncStatus: 'deleted' as const,
        _lastModified: Date.now(),
        updated_at: new Date().toISOString()
      });
      OfflineLogger.info('ProductRepository', `Marked product as deleted: ${id}`);
    } catch (error) {
      OfflineLogger.error('ProductRepository.delete', error);
      throw error;
    }
  }

  // ✅ NUOVO: Eliminazione fisica del prodotto (per sync delle eliminazioni dal server)
  async physicalDelete(id: string): Promise<void> {
    try {
      await this.dbTable.delete(id);
      OfflineLogger.info('ProductRepository', `Physically deleted product: ${id}`);
    } catch (error) {
      OfflineLogger.error('ProductRepository.physicalDelete', error);
      throw error;
    }
  }

  async deleteByCategory(categoryId: string, physical: boolean = false): Promise<void> {
    try {
      const products = await this.dbTable.where('category_id').equals(categoryId).toArray();
      for (const product of products) {
        if (physical) {
          await this.physicalDelete(product.id);
        } else {
          await this.delete(product.id);
        }
      }
      OfflineLogger.info('ProductRepository', `Deleted ${products.length} products for category ${categoryId}`);
    } catch (error) {
      OfflineLogger.error('ProductRepository.deleteByCategory', error);
      throw error;
    }
  }

  async getPendingOperations(): Promise<SyncableUserProduct[]> {
    try {
      return await this.dbTable
        .where('_syncStatus')
        .anyOf(['pending', 'deleted'])
        .toArray();
    } catch (error) {
      OfflineLogger.error('ProductRepository.getPendingOperations', error);
      return [];
    }
  }

  async markAsSynced(ids: string[]): Promise<void> {
    try {
      for (const id of ids) {
        await this.dbTable.update(id, {
          _syncStatus: 'synced' as const,
          _lastModified: Date.now()
        });
      }
      OfflineLogger.info('ProductRepository', `Marked ${ids.length} products as synced`);
    } catch (error) {
      OfflineLogger.error('ProductRepository.markAsSynced', error);
      throw error;
    }
  }

  async removeDeletedItems(): Promise<void> {
    try {
      const deletedItems = await this.dbTable
        .where('_syncStatus')
        .equals('deleted')
        .toArray();
      
      for (const item of deletedItems) {
        await this.dbTable.delete(item.id);
      }
      
      OfflineLogger.info('ProductRepository', `Physically removed ${deletedItems.length} deleted products`);
    } catch (error) {
      OfflineLogger.error('ProductRepository.removeDeletedItems', error);
      throw error;
    }
  }

  async normalizePhotoUrls(): Promise<void> {
    try {
      const all = await this.dbTable.toArray();
      for (const item of all) {
        if (item.photo && item.photo.startsWith('product-photos/https://')) {
          await this.dbTable.update(item.id, { photo: normalizePhotoUrl(item.photo) });
        }
      }
      OfflineLogger.info('ProductRepository', 'Normalized photo URLs');
    } catch (error) {
      OfflineLogger.error('ProductRepository.normalizePhotoUrls', error);
    }
  }

  // Helper method to identify offline-created products
  private isOfflineCreatedProduct(id: string): boolean {
    return id.startsWith('offline-') || (id.includes('-') && id.length > 30);
  }

  // ✅ NUOVO: Trova duplicati per contenuto con algoritmo migliorato
  private async findDuplicateByContent(product: UserProduct): Promise<SyncableUserProduct | null> {
    try {
      const normalizedName = normalizeProductName(product.name);
      const normalizedCategoryId = product.categoryId ?? null;
      const candidates = await this.dbTable
        .where('normalized_name')
        .equals(normalizedName)
        .filter(item => {
          if (item._syncStatus === 'deleted') return false;

          const candidateCategory = item.category_id ?? null;
          const sameCategory = candidateCategory === normalizedCategoryId;
          const sameUser = (item.user_id || '') === (product.userId || '');

          if (item.normalized_name) {
            return sameCategory && sameUser;
          }

          const fallbackNormalized = normalizeProductName(item.name);
          return (
            sameCategory &&
            sameUser &&
            fallbackNormalized === normalizedName
          );
        })
        .toArray();

      // Cerca un duplicato esatto per contenuto
      for (const candidate of candidates) {
        const isSameContent =
          (candidate.normalized_name || normalizeProductName(candidate.name)) === normalizedName &&
          candidate.barcode === product.barcode &&
          candidate.category_id === product.categoryId &&
          candidate.default_quantity === product.defaultQuantity &&
          candidate.default_unit === product.defaultUnit;
          
        if (isSameContent) {
          return candidate;
        }
      }
      
      return null;
    } catch (error) {
      OfflineLogger.error('ProductRepository.findDuplicateByContent', error);
      return null;
    }
  }

  // ✅ MIGLIORATO: Algoritmo più intelligente per rimuovere duplicati
  private removeDuplicatesIntelligent(products: SyncableUserProduct[]): SyncableUserProduct[] {
    const seen = new Map<string, SyncableUserProduct>();
    const duplicateGroups = new Map<string, SyncableUserProduct[]>();
    
    // Prima passata: raggruppa per contenuto
    for (const product of products) {
      const contentKey = this.generateContentKey(product);
      
      if (!duplicateGroups.has(contentKey)) {
        duplicateGroups.set(contentKey, []);
      }
      duplicateGroups.get(contentKey)!.push(product);
    }
    
    // Seconda passata: per ogni gruppo, seleziona il migliore
    for (const [contentKey, group] of duplicateGroups) {
      if (group.length === 1) {
        seen.set(contentKey, group[0]);
      } else {
        // ✅ NUOVO: Logica di selezione intelligente per duplicati
        const best = this.selectBestProduct(group);
        seen.set(contentKey, best);
        
        OfflineLogger.info('ProductRepository', `Deduplication: selected best of ${group.length} duplicates for ${best.name}`);
      }
    }
    
    return Array.from(seen.values());
  }

  // ✅ NUOVO: Genera chiave univoca per il contenuto del prodotto
  private generateContentKey(product: SyncableUserProduct): string {
    const normalizedName = product.normalized_name || normalizeProductName(product.name);
    const normalizedCategory = product.category_id ?? 'no-category';
    const normalizedUserId = product.user_id || 'no-user';
    const normalizedBarcode = product.barcode || 'no-barcode';
    const quantityKey = typeof product.default_quantity === 'number' ? product.default_quantity : 'no-quantity';
    const unitKey = product.default_unit || 'no-unit';

    return [
      normalizedUserId,
      normalizedCategory,
      normalizedName,
      normalizedBarcode,
      quantityKey,
      unitKey
    ].join('|');
  }

  // ✅ NUOVO: Seleziona il prodotto migliore tra i duplicati
  private selectBestProduct(products: SyncableUserProduct[]): SyncableUserProduct {
    // Ordina per preferenza: synced > pending > deleted, poi per lastModified
    return products.sort((a, b) => {
      // Preferenza per status
      const statusPriority = { 'synced': 3, 'pending': 2, 'deleted': 1 };
      const statusDiff = (statusPriority[b._syncStatus] || 0) - (statusPriority[a._syncStatus] || 0);
      
      if (statusDiff !== 0) return statusDiff;
      
      // Se stesso status, preferisci il più recente
      return b._lastModified - a._lastModified;
    })[0];
  }

  // ✅ MIGLIORATO: Cleanup orfani più efficace
  async cleanupOrphanedRecords(): Promise<void> {
    try {
      const allProducts = await this.dbTable.toArray();
      const duplicateGroups = new Map<string, SyncableUserProduct[]>();
      
      // Raggruppa per contenuto
      for (const product of allProducts) {
        const key = this.generateContentKey(product);
        if (!duplicateGroups.has(key)) {
          duplicateGroups.set(key, []);
        }
        duplicateGroups.get(key)!.push(product);
      }
      
      let removedCount = 0;
      
      // Per ogni gruppo, mantieni solo il migliore
      for (const [key, group] of duplicateGroups) {
        if (group.length > 1) {
          const best = this.selectBestProduct(group);
          
          // Rimuovi tutti tranne il migliore
          for (const product of group) {
            if (product.id !== best.id) {
              await this.dbTable.delete(product.id);
              removedCount++;
            }
          }
        }
      }
      
      OfflineLogger.info('ProductRepository', `Cleaned up ${removedCount} orphaned records`);
    } catch (error) {
      OfflineLogger.error('ProductRepository.cleanupOrphanedRecords', error);
    }
  }

  // ✅ NUOVO: Metodo per statistiche e debug
  async getDebugStats(): Promise<{
    total: number;
    synced: number;
    pending: number;
    deleted: number;
    duplicates: number;
  }> {
    try {
      const allProducts = await this.dbTable.toArray();
      const stats = {
        total: allProducts.length,
        synced: allProducts.filter(p => p._syncStatus === 'synced').length,
        pending: allProducts.filter(p => p._syncStatus === 'pending').length,
        deleted: allProducts.filter(p => p._syncStatus === 'deleted').length,
        duplicates: 0
      };
      
      // Calcola duplicati
      const contentKeys = new Set<string>();
      let duplicateCount = 0;
      
      for (const product of allProducts) {
        const key = this.generateContentKey(product);
        if (contentKeys.has(key)) {
          duplicateCount++;
        } else {
          contentKeys.add(key);
        }
      }
      
      stats.duplicates = duplicateCount;
      
      return stats;
    } catch (error) {
      OfflineLogger.error('ProductRepository.getDebugStats', error);
      return { total: 0, synced: 0, pending: 0, deleted: 0, duplicates: 0 };
    }
  }
}

export const productRepository = new ProductRepository();
